// src/App.jsx — application shell component. Replaces the inline `function App()`
// that lived in `Lift Log App.html` (lines 191-221 in v2.0). Same component tree,
// but rendered full-viewport (100dvh) instead of inside the 390×844 LLPhone frame
// that showcased the design in the browser during v2.0 development.
//
// Phase 27 Stage C (VITE-03). StrictMode wrap is added by Stage D.

import React from 'react';
import { AppProvider } from './store.jsx';
import { AppRouter, ActiveTabBar, ToastStack, ThemeInjector, PhotoDBInit } from './shell.jsx';
import { LLFonts } from './tokens.jsx';
import { PWAInit, UpdateBanner } from './pwa.jsx';
import { A2HSPrompt } from './a2hs.jsx';
import { RestFlashOverlay } from './timers.jsx';
import { WorkoutMiniBar, RestWatcher } from './active.jsx';
import { ErrorBoundary, DataAlerts } from './error_boundary.jsx';

export function App() {
  return (
    <>
      <LLFonts />
      {/* Outer boundary: a crash in the provider / overlays (not just a screen)
          still gets the recovery screen instead of a blank page. */}
      <ErrorBoundary>
      <AppProvider>
        <ThemeInjector />
        <PhotoDBInit />
        <PWAInit />
        <UpdateBanner />
        <A2HSPrompt />
        <RestFlashOverlay />
        <RestWatcher />
        <div style={{
          position: 'relative',
          width: '100%',
          height: '100dvh',
          // Every screen root is `flex: 1` and expects a flex-column parent.
          // v2.0 supplied that via LLPhone's content area (display:flex;
          // flexDirection:column); the Phase 27 Vite migration replaced LLPhone
          // with this div but dropped the column, leaving `flex: 1` inert — so
          // screens taller than the viewport (e.g. the program editor) overflowed
          // 100dvh and clipped their bottom with no way to scroll. Restore it.
          display: 'flex',
          flexDirection: 'column',
          // clip (not hidden): an overflow:hidden box can still be scrolled
          // programmatically (focus/scrollIntoView inside a sheet), which shifted
          // the whole app up and left a blank band at the bottom.
          overflow: 'clip',
          color: 'var(--fg, #e5e7eb)',
        }}>
          <ErrorBoundary>
            <AppRouter />
          </ErrorBoundary>
          <WorkoutMiniBar />
          <ActiveTabBar />
          <ToastStack />
        </div>
        <DataAlerts />
      </AppProvider>
      </ErrorBoundary>
    </>
  );
}
